import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';

import { Icon } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';

import theme from '../../../../../themes/colors';

interface PaymentDetailsCardProps {
  paymentInfo: any;
  status: string;
  totalPaidAmount: string;
  totalSettledAmount: string;
  closedAmount?: string;
  isLoading?: boolean;
  totalDiscountAmount?: string;
}

const PaymentDetailsCard = ({
  paymentInfo,
  status,
  totalPaidAmount,
  totalSettledAmount,
  closedAmount,
  isLoading = false,
  totalDiscountAmount
}: PaymentDetailsCardProps) => {
  const { currencyConfig } = useSelector((state: any) => state.user);

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const keyStyle = ' text-grey text-sm mt-2';
  const valueStyle =
    'ml-auto text-base text-content font-medium flex text-right mt-auto mb-auto';

  const getStatusStyles = () => {
    if (paymentInfo.status === 'Pending') return 'bg-yellowLite2 text-yellow20';
    if (paymentInfo.status === 'Closed') return 'bg-error10 text-error';
    if (paymentInfo.status === 'Discount')
      return 'bg-cofeeTeal50 text-cofeeTeal300';
    if (Number(totalPaidAmount) === Number(totalSettledAmount))
      return 'bg-primary10 text-primary40';
    if (Number(totalPaidAmount) > Number(totalSettledAmount))
      return 'bg-blue text-blue1';
    return '';
  };

  return (
    <div className="border-b border-grey10 py-3 last:border-none ">
      <div key={paymentInfo.title} className="w-full gap-4">
        {isLoading ? (
          <div className="w-full">
            <Skeleton
              key={Math.random()}
              animation="wave"
              className="size-full"
            />
          </div>
        ) : (
          <div className="flex justify-between">
            <div className="flex flex-col">
              {status && (
                <span
                  className={`w-max rounded-md px-2.5 text-sm 
                  ${getStatusStyles()}`}
                >
                  {paymentInfo.status}
                </span>
              )}
              <span className={keyStyle}>{paymentInfo.title}</span>
              {paymentInfo.status !== 'Closed' && (
                <span className={keyStyle}>{paymentInfo.dueDate}</span>
              )}
              {paymentInfo.status === 'Closed' && (
                <>
                  <span className={keyStyle}>Closed on</span>
                  <span className={keyStyle}>Notes</span>
                </>
              )}
            </div>
            <div className="flex flex-col text-right">
              <div
                className={`${valueStyle} ${paymentInfo.className} flex items-center justify-center `}
              >
                <div
                  className={`flex items-center justify-center ${
                    (Number(closedAmount) > 0 && paymentInfo.subValue) ||
                    paymentInfo.status === 'Closed' ||
                    (paymentInfo.title === 'Total Amount Requested' &&
                      Number(totalDiscountAmount ?? 0) > 0)
                      ? 'mr-2 text-grey line-through'
                      : ''
                  }`}
                >
                  {paymentInfo.status === 'Discount' ? (
                    <span className="mr-0.5">-</span>
                  ) : (
                    ''
                  )}
                  <CurrencyAmount
                    value={paymentInfo.value}
                    className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                    symbolClass={`${
                      isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                    }`}
                    valueClass=""
                  />
                </div>
                {(Number(closedAmount) > 0 ||
                  (paymentInfo.title === 'Total Amount Requested' &&
                    Number(totalDiscountAmount ?? 0) > 0)) &&
                  paymentInfo.subValue && (
                    <div className="flex items-center justify-center">
                      <CurrencyAmount
                        value={paymentInfo.subValue}
                        className={`${
                          isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                        }`}
                        symbolClass={`${
                          isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                        }`}
                        valueClass=""
                      />
                    </div>
                  )}
              </div>
              {paymentInfo.status === 'Closed' && (
                <>
                  <span className={keyStyle}>{paymentInfo.dueDate}</span>
                  <span className={keyStyle}>{paymentInfo.subText}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {paymentInfo.subText && paymentInfo.status !== 'Closed' && (
        <div className="flex items-center gap-1.5 text-sm font-normal text-grey">
          <Icon
            name="alert-circle"
            size="none"
            className="size-3.5"
            stroke={theme.grey}
          />
          {paymentInfo.subText}
        </div>
      )}
    </div>
  );
};

export default PaymentDetailsCard;
